"use client";

import { CheckCircle2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { formatUnits } from "viem";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Toggle button pair for vote source selection — mirrors DAO election SourcePicker.
 *
 * Two buttons: wallet (آزاد) and staked (سپرده گذاری شده).
 * Selected state: bg-text-primary + text-text-label + CheckCircle2 endIcon.
 * isError: both buttons show "—" + error caption below.
 */

type VoteSource = "wallet" | "stake";

interface SourcePickerProps {
  walletBalance: bigint;
  stakedAvailable: bigint;
  selected: VoteSource | undefined;
  onSelect: (source: VoteSource) => void;
  isError: boolean;
}

/** Format wei → decimal string (trailing-zero stripped, max 3dp for display). */
function formatDnmDisplay(wei: bigint): string {
  const formatted = formatUnits(wei, 18);
  return formatted.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}

export const SourcePicker: React.FC<SourcePickerProps> = ({
  walletBalance,
  stakedAvailable,
  selected,
  onSelect,
  isError,
}) => {
  const t = useTranslations("statement");
  const walletLabel = isError
    ? "—"
    : t("vote.walletLabel", {
        amount: formatDnmDisplay(walletBalance),
      });
  const stakedLabel = isError
    ? "—"
    : t("vote.stakedLabel", {
        amount: formatDnmDisplay(stakedAvailable),
      });

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex flex-row items-stretch justify-center gap-4 w-full">
        {/* Source order: first JSX child renders physical-RIGHT under dir=rtl */}
        {/* Right (start): wallet — "آزاد" */}
        <Button
          type="button"
          variant="faded"
          colors="secondary"
          size="md"
          radius="xs"
          className={cn(
            "flex-1",
            selected === "wallet" && "bg-text-primary text-text-label",
          )}
          endIcon={
            selected === "wallet" ? (
              <CheckCircle2 className="size-5" aria-hidden="true" />
            ) : null
          }
          onClick={() => onSelect("wallet")}
        >
          {walletLabel}
        </Button>

        {/* Left (end): staked — "سپرده گذاری شده" (Q7 dual-phrasing verbatim) */}
        <Button
          type="button"
          variant="faded"
          colors="secondary"
          size="md"
          radius="xs"
          className={cn(
            "flex-1",
            selected === "stake" && "bg-text-primary text-text-label",
          )}
          endIcon={
            selected === "stake" ? (
              <CheckCircle2 className="size-5" aria-hidden="true" />
            ) : null
          }
          onClick={() => onSelect("stake")}
        >
          {stakedLabel}
        </Button>
      </div>

      {isError && (
        <span className="text-xs text-text-secondary mt-2">
          {/* EC-14 error caption */}
          {t("vote.readFailed")}
        </span>
      )}
    </div>
  );
};
